import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { SubtitleCue, parseWebVTT } from './SubtitleParser';
import { AddendumTrack } from '../types';
import { languageName } from './languageNames';

function correctTimestampOffset(cues: SubtitleCue[]): SubtitleCue[] {
  if (cues.length === 0) return cues;

  const minStart = Math.min(...cues.map(c => c.startTime));
  if (minStart < 3600) return cues;

  const offset = Math.floor(minStart / 3600) * 3600;
  console.warn(`[Subtitles] Detected timestamp offset of ${offset}s, auto-correcting`);

  return cues.map(c => ({
    ...c,
    startTime: c.startTime - offset,
    endTime: c.endTime - offset,
  }));
}

function getCueAndIndexAtTime(cues: SubtitleCue[], time: number): { cue: SubtitleCue | null; index: number | null } {
  for (let i = 0; i < cues.length; i++) {
    if (time >= cues[i].startTime && time < cues[i].endTime) {
      return { cue: cues[i], index: i };
    }
  }
  return { cue: null, index: null };
}

const HOLDOVER_MS = 1000;

export function useSubtitles(
  subtitleUrl: string | undefined,
  currentTime: number,
  speakerAttributions?: Record<string, string>,
  subtitleOffset: number = 0,
) {
  const [cues, setCues] = useState<SubtitleCue[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!subtitleUrl) {
      setCues([]);
      setIsLoaded(false);
      return;
    }

    setIsLoaded(false);
    const token = localStorage.getItem('forum_token');
    fetch(subtitleUrl, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined)
      .then(res => {
        if (!res.ok) {
          throw new Error(`Failed to fetch subtitles: HTTP ${res.status}`);
        }
        return res.text();
      })
      .then(text => {
        const parsed = correctTimestampOffset(parseWebVTT(text));
        if (parsed.length > 0) {
          const first = parsed[0];
          const last = parsed[parsed.length - 1];
          console.log(`[Subtitles] Loaded ${parsed.length} cues, range: ${first.startTime.toFixed(2)}s - ${last.endTime.toFixed(2)}s`);
        }
        setCues(parsed);
        setIsLoaded(true);
      })
      .catch(err => {
        console.error('[Subtitles] Failed to load:', err);
        setCues([]);
        setIsLoaded(false);
      });
  }, [subtitleUrl]);

  // Compute the raw cue at the current time
  const rawResult = useMemo(() => {
    const adjustedTime = currentTime + subtitleOffset;
    const { cue, index } = getCueAndIndexAtTime(cues, adjustedTime);
    if (cue && index !== null && speakerAttributions && speakerAttributions[String(index)]) {
      return {
        cue: { ...cue, speaker: speakerAttributions[String(index)] },
        index,
      };
    }
    return { cue, index };
  }, [cues, currentTime, speakerAttributions, subtitleOffset]);

  // Holdover logic: keep displaying the previous cue for up to 1s during gaps
  // so subtitles don't flicker when there's a brief pause between cue blocks.
  const [displayCue, setDisplayCue] = useState<SubtitleCue | null>(null);
  const [displayIndex, setDisplayIndex] = useState<number | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (rawResult.cue !== null) {
      // Active cue — show immediately, cancel any pending hide
      clearHideTimer();
      setDisplayCue(rawResult.cue);
      setDisplayIndex(rawResult.index);
    } else if (displayCue !== null && !hideTimerRef.current) {
      // Entered a gap — start a holdover timer
      hideTimerRef.current = setTimeout(() => {
        setDisplayCue(null);
        setDisplayIndex(null);
        hideTimerRef.current = null;
      }, HOLDOVER_MS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawResult.cue, rawResult.index, clearHideTimer]);

  // Reset display state when cues change (new subtitle file loaded)
  useEffect(() => {
    clearHideTimer();
    setDisplayCue(null);
    setDisplayIndex(null);
  }, [cues, clearHideTimer]);

  // Clean up timer on unmount
  useEffect(() => {
    return () => clearHideTimer();
  }, [clearHideTimer]);

  // Two people at once (api/pkg/data/overlap.go): a split leaves lines with
  // exactly the displayed cue's times. They show together, stacked; the
  // displayed cue stays the one that drives Follow, 3D audio and the radar.
  const simultaneousCues = useMemo(
    () => withSpeakers(simultaneousWith(cues, displayIndex), cues, speakerAttributions),
    [cues, displayIndex, speakerAttributions],
  );

  return { currentCue: displayCue, currentCueIndex: displayIndex, simultaneousCues, cues, isLoaded };
}

/**
 * Indices of the cues running at exactly the same time as cues[index] — the
 * other halves of a split line — excluding index itself, in order.
 */
export function simultaneousWith(cues: SubtitleCue[], index: number | null): number[] {
  if (index === null || !cues[index]) return [];
  const { startTime, endTime } = cues[index];
  const out: number[] = [];
  // A split puts its halves next to each other, so look outwards and stop
  // at the first cue that differs.
  for (let i = index - 1; i >= 0 && cues[i].startTime === startTime && cues[i].endTime === endTime; i--) out.unshift(i);
  for (let i = index + 1; i < cues.length && cues[i].startTime === startTime && cues[i].endTime === endTime; i++) out.push(i);
  return out;
}

function withSpeakers(indices: number[], cues: SubtitleCue[], attributions?: Record<string, string>): SubtitleCue[] {
  return indices.map((i) => {
    const speaker = attributions?.[String(i)];
    return speaker ? { ...cues[i], speaker } : cues[i];
  });
}

// ---------------------------------------------------------------------------
// T18 (docs/plans/2026-08-13-verification-integration-closeout.md) — the
// subtitle picker's data model: base and addendum tracks merged into one
// list, visually distinguishable.
//
// This is deliberately kept separate from the hook above rather than folded
// into it: `useSubtitles` fetches and parses whichever single URL it is
// given, exactly as it always has, and that code is untouched by this
// section. A video with no addenda passes `mergeSubtitleTracks` an empty (or
// undefined) addenda list and gets back precisely its base languages, in the
// order given — the superset invariant (Decision 2 of the closeout plan)
// holds by construction, not by a conditional that has to be kept correct.
//
// D11: every language is compared and stored in normalized (trimmed,
// lower-case) form — the same rule T5/T14/useVideoVerification.ts apply —
// because a mis-cased code has already produced one real shadowing hole on
// chain (see D11 in the Discovered Issues Log).

export type SubtitleTrackKind = 'base' | 'addendum';

/**
 * One entry in the merged subtitle picker.
 *
 * `addedAt` and `anchored` only ever mean something for an addendum:
 *  - anchored addendum: `addedAt` is the CHAIN's clock
 *    (AddendumAnchor.AddedAt), never CreatedAt (when we merely built the
 *    file) — see AddendumAnchor's doc comment in types.ts.
 *  - unanchored addendum (`track.chain` is null): there is no on-chain hash
 *    to verify this track against and no chain timestamp for it. Per D18 in
 *    the Discovered Issues Log, that is a THIRD state, not equivalent to a
 *    verified addendum and not the same as a rejected one — it is neither
 *    proven nor disproven. `anchored` is false and `addedAt` is null so a
 *    caller can render "added by the publisher — not yet confirmed on
 *    chain" rather than silently showing a date with no chain backing.
 *  - base track: always `anchored: true` (it is covered by the root hash by
 *    construction) and `addedAt: null` — it was not "added" at a point in
 *    time distinct from the seal, so there is nothing to date.
 */
export interface SubtitleTrackOption {
  /** Normalized (trimmed, lower-case) language code. */
  lang: string;
  kind: SubtitleTrackKind;
  label: string;
  addedAt: Date | null;
  anchored: boolean;
}

const normalizeLang = (lang: string): string => lang.trim().toLowerCase();

/**
 * Shared date rendering for an addendum's chain `added_at`, so the picker
 * (VideoPlayer.tsx's Language flyout) and the per-cue badge
 * (SubtitleOverlay.tsx) never show the same track two different dates.
 */
export function formatAddedDate(date: Date): string {
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * Merges the sealed base's subtitle languages with any addendum tracks into
 * one list for the picker, per T18's acceptance criteria:
 *  - base and addendum tracks appear together, visually distinguishable via
 *    `kind`/`anchored`/`addedAt`;
 *  - a language in `rejectedAddenda` (bytes that failed their own on-chain
 *    hash — see useVideoVerification.ts's PlaybackPolicy) is dropped
 *    entirely, never shown;
 *  - base tracks are listed exactly as given, in order — passing no addenda
 *    reproduces the base list unchanged, which is the regression guard for
 *    every video that has none.
 *
 * `rejectedAddenda` is taken as a plain string array rather than imported
 * from `frontend/src/verification/`, so this module — used by every video
 * page, including admin editors with no chain/wallet context — never
 * depends on the verification module.
 */
export function mergeSubtitleTracks(
  baseLanguages: string[] | undefined,
  addenda: AddendumTrack[] | undefined,
  rejectedAddenda: string[] | undefined,
): SubtitleTrackOption[] {
  const base: SubtitleTrackOption[] = (baseLanguages ?? []).map((lang) => {
    const normalized = normalizeLang(lang);
    return {
      lang: normalized,
      kind: 'base',
      label: languageName(normalized),
      addedAt: null,
      anchored: true,
    };
  });

  const baseLangs = new Set(base.map((track) => track.lang));
  const rejected = new Set((rejectedAddenda ?? []).map(normalizeLang));
  const seen = new Set<string>();

  const addendaTracks: SubtitleTrackOption[] = [];
  for (const track of addenda ?? []) {
    const lang = normalizeLang(track.lang);
    // A language can never be both sealed and an addendum (§3.7: "one track
    // per language, ever"), and an addendum language can never appear
    // twice — both structurally enforced on chain. These checks are
    // defensive rather than load-bearing: if the data ever disagreed, the
    // sealed base wins and a duplicate is deduped instead of shown twice.
    if (baseLangs.has(lang) || rejected.has(lang) || seen.has(lang)) continue;
    seen.add(lang);
    addendaTracks.push({
      lang,
      kind: 'addendum',
      label: languageName(lang),
      addedAt: track.chain ? new Date(track.chain.added_at) : null,
      anchored: !!track.chain,
    });
  }

  return [...base, ...addendaTracks];
}

/**
 * Tolerates the `VideoPlayer` `subtitleLanguages` prop's two legal shapes so
 * the component never has to branch on which one it was given: a plain
 * string (what every caller before T18 passes, and every caller after it
 * that has no addenda) always meant a base track, so that is exactly what it
 * normalizes to — `kind: 'base'`, no `addedAt`, `anchored: true`. A
 * `SubtitleTrackOption` (what `mergeSubtitleTracks` produces) passes through
 * unchanged.
 *
 * This is the regression guard at the point VideoPlayer actually consumes
 * the prop: a caller passing plain strings (every caller with no addenda)
 * gets back precisely the base-track list `mergeSubtitleTracks` would have
 * produced for those same languages with no addenda — see
 * `useSubtitles.test.ts`'s "regression" test by that name.
 */
export function normalizeSubtitleTracks(entries: (string | SubtitleTrackOption)[]): SubtitleTrackOption[] {
  return entries.map((entry) =>
    typeof entry === 'string'
      ? {
          lang: normalizeLang(entry),
          kind: 'base' as const,
          label: languageName(normalizeLang(entry)),
          addedAt: null,
          anchored: true,
        }
      : entry,
  );
}
